import { conditions, controllbarIconSize, responseValidator } from "helpers";
import IMAGES from "../../../../../images/importImages";
import { ConditionName } from "interfaces";
import { useAppDispatch, useAppSelector } from "redux-share/hooks";
import { setPlayListPopover } from "redux-share/slices/popoversSlice";
import Image from "next/image";
import { useState, useEffect, useRef, memo } from "react";
import { shallowEqual } from "react-redux";
import useIntersect from "helpers/hooks/useIntersect";
import PlayListVideoCards from "./playlistVideoCards";
import { playListClickLog, sendFluentLog } from "logs";
import { getPlaylistVideos, handlePlayListTabClick } from "redux-share/actions";
import dynamic from "next/dynamic";
import CloseIcon from "../../icon-components/closeIcon";

const Popover = dynamic(() => import("../popover/popover"));
const TouchScreenPlaylistObserver = dynamic(() => import("./touchScreenPlaylistObserver"));

const PlayList = () => {
    const dispatch = useAppDispatch();
    const { playList } = useAppSelector(
        ({ videoData: { params, playList, isComplexDesktop, isComplexPortrait, videoElementSizeInfo } }) => ({
            params,
            playList,
            isComplexDesktop,
            isComplexPortrait,
            videoElementSizeInfo
        }),
        shallowEqual
    );
    const { playListPopover } = useAppSelector(({ popovers: { playListPopover } }) => ({ playListPopover }), shallowEqual);
    useAppSelector(({ controller: { isFullScreen } }) => ({ isFullScreen }), shallowEqual);
    const [scrollEnd, setScrollEnd] = useState<boolean>(false);
    const [videoPlaylist, setVideoPlaylist] = useState<{ video: any; order: number }[] | undefined>(undefined);
    const [selectedPlaylist, setSelectedPlaylist] = useState<number>(playList![0].id);
    const videoPlaylistRef = useRef<any>(null);

    useEffect(() => {
        if (
            scrollEnd &&
            (videoPlaylist === undefined || (playList!.find((item: any) => item.id === selectedPlaylist) as any).count !== videoPlaylist.length)
        )
            fetchData();
        else setScrollEnd(false);
    }, [scrollEnd]);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (!!videoPlaylist && conditions(ConditionName.isComplexDesktop) && !conditions(ConditionName.isFullScreen))
            videoPlaylistRef.current = videoPlaylist;
    }, [videoPlaylist]);

    const fetchData = async () => {
        const videoPlaylistTemp = videoPlaylist !== undefined ? videoPlaylist : [];
        const { data, status } = await getPlaylistVideos(selectedPlaylist, videoPlaylistTemp.length);
        if (responseValidator(status)) {
            setVideoPlaylist([...videoPlaylistTemp, ...data.results]);
        }
        setScrollEnd(false);
    };

    const handleIntersect = ([entry]: IntersectionObserverEntry[]) => {
        if (entry.isIntersecting && conditions(ConditionName.isComplexDesktop) && !!videoPlaylistRef.current) setScrollEnd(true);
    };

    const observerRef = useIntersect(handleIntersect);

    const handleCloseModal = () => {
        dispatch(setPlayListPopover(false));
        setVideoPlaylist(undefined);
    };

    const customHeader = (
        <div className="pl-flex pl-justify-between pl-items-center pl-gap-2 pl-py-3">
            <p className={`pl-text-[16px] pl-font-medium pl-text-black dark:pl-text-gray-100`}>
                {playList!.length === 1 ? `لیست پخش "${playList![0].title}"` : "لیست پخش"}
            </p>
            <div className={`pl-rounded-[4px] pl-p-1 pl-cursor-pointer pl-bg-gray-100`} onClick={handleCloseModal}>
                <CloseIcon className={`pl-w-3 pl-h-3 pl-stroke-gray-800`} />
            </div>
        </div>
    );

    const playListVideoCarts = (
        <PlayListVideoCards
            onClick={(item: any) => {
                setSelectedPlaylist(item);
                setVideoPlaylist(undefined);
                sendFluentLog(playListClickLog(item));
            }}
            playList={playList}
            scrollEnd={scrollEnd}
            selectedPlaylist={selectedPlaylist}
            videoPlaylist={videoPlaylist}
        />
    );

    let playListContent: unknown = null;
    if (conditions(ConditionName.isFullScreen)) {
        playListContent = (
            <>
                <div className="pl-flex pl-items-center pl-cursor-pointer" onClick={handlePlayListTabClick}>
                    <Image
                        src={IMAGES.playList}
                        alt="playList-icon"
                        width={
                            conditions(ConditionName.isFullMobilePortrait) || !conditions(ConditionName.isMiniPlayer)
                                ? controllbarIconSize.other
                                : controllbarIconSize.miniPlayer
                        }
                        height={
                            conditions(ConditionName.isFullMobilePortrait) || !conditions(ConditionName.isMiniPlayer)
                                ? controllbarIconSize.other
                                : controllbarIconSize.miniPlayer
                        }
                    />
                </div>
                <Popover
                    status={playListPopover}
                    onClose={handleCloseModal}
                    isHeader={true}
                    customHeader={customHeader}
                    isBlurBackground={true}
                    type="modal"
                    classNames={`!pl-max-w-[340px] dvh pl-rounded-r-none ${conditions(ConditionName.isDarkMode) ? "!pl-bg-dark-3" : "!pl-bg-white"}`}
                    animation={!!playListPopover ? "animate__fadeInRight" : "animate__fadeOutRight"}
                    parentClassNames="!pl-justify-start"
                >
                    <div className="pl-flex pl-flex-col pl-gap-4 pl-mb-4">
                        {playListVideoCarts}
                        {playListPopover && <TouchScreenPlaylistObserver setScrollEnd={setScrollEnd} />}
                    </div>
                </Popover>
            </>
        );
    } else if (conditions(ConditionName.isTouchScreenPortrait)) {
        playListContent = (
            <>
                <Popover
                    classNames={`!pl-p-0 pl-left-0`}
                    status={playListPopover}
                    onClose={() => {
                        dispatch(setPlayListPopover(false));
                        setVideoPlaylist(undefined);
                    }}
                    ref={undefined}
                    isFullHeight={true}
                    headerTitle={playList!.length === 1 ? `لیست پخش "${playList![0].title}"` : "لیست پخش"}
                    isHeader={true}
                    customHeader={customHeader}
                    type="modal"
                    isHasCatchUpLineOnTop={true}
                >
                    <div className="pl-flex pl-flex-col pl-gap-4 pl-mb-4">
                        {playListVideoCarts}
                        {playListPopover && <TouchScreenPlaylistObserver setScrollEnd={setScrollEnd} />}
                    </div>
                </Popover>
            </>
        );
    } else if (conditions(ConditionName.isComplexDesktop)) {
        playListContent = (
            <>
                <div className="pl-overflow-auto pl-flex pl-flex-col pl-gap-4 pl-mb-4">
                    <p className="pl-text-[16px] pl-font-bold pl-text-secondary-900 dark:pl-text-secondary-50 pl-border-b pl-border-secondary-400 pl-pb-3 pl-ml-6">
                        {playList!.length === 1 ? `لیست پخش "${playList![0].title}"` : "لیست های پخش"}
                    </p>
                    {playListVideoCarts}
                    <div id="observer" className="pl-bg-transparent pl-pt-5" ref={observerRef} />
                </div>
                <div className="pl-w-auto pl-h-[1px] pl-bg-secondary-400 dark:pl-bg-secondary-100 pl-ml-6" />
            </>
        );
    }

    return <>{playListContent}</>;
};

export default memo(PlayList);
